// src/pages/SongPage.js

import React, { useEffect, useState, useContext, useRef, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { getSongById } from '../utils/songUtils';
import { getSignedUrl } from '../utils/getSignedUrl';
import { AuthContext } from '../context/AuthContext';
import { FaStar, FaFire, FaCrown, FaPause, FaPlay } from 'react-icons/fa';
import { tagColors, tagIcons } from '../constants/tagConfig';
import { useSongContext } from '../SongContext';
import { generateWaveformData } from '../utils/audioWaveform';
import audioService from '../utils/audioService';
import SimilarStockSection from '../components/SimilarStockSection';

// Add polyfill for requestIdleCallback
const requestIdleCallbackPolyfill = (callback) => {
  const start = Date.now();
  return setTimeout(() => {
    callback({
      didTimeout: false,
      timeRemaining: () => Math.max(0, 50 - (Date.now() - start))
    });
  }, 1);
};

// Use the native version if available, otherwise use the polyfill
const requestIdle = window.requestIdleCallback || requestIdleCallbackPolyfill;

const shimmer = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
`;

// Global queue for waveform generation
const waveformQueue = [];
let isProcessingQueue = false;
const MAX_CONCURRENT_WAVEFORMS = 3;
let activeWaveformCount = 0;

// Process waveform generation queue
const processWaveformQueue = async () => {
  if (isProcessingQueue || waveformQueue.length === 0 || activeWaveformCount >= MAX_CONCURRENT_WAVEFORMS) {
    return;
  }

  isProcessingQueue = true;

  while (waveformQueue.length > 0 && activeWaveformCount < MAX_CONCURRENT_WAVEFORMS) {
    const { signedUrl, setWaveformData, setIsLoading, markComplete } = waveformQueue[0];
    
    try {
      activeWaveformCount++;
      const waveform = await generateWaveformData(signedUrl);
      setWaveformData(waveform);
    } catch (error) {
      console.error('Error generating waveform:', error);
    } finally {
      activeWaveformCount--;
      setIsLoading(false);
      markComplete();
      waveformQueue.shift();
    }
  }

  isProcessingQueue = false;

  // If there are still items in the queue and we have capacity, continue processing
  if (waveformQueue.length > 0 && activeWaveformCount < MAX_CONCURRENT_WAVEFORMS) {
    setTimeout(processWaveformQueue, 0);
  }
};

// Styled Components
const PageContainer = styled.div`
  max-width: 100%;
  overflow-x: hidden;
  background-color: #E3E3E3;
  min-height: 100vh;
  border-radius: 12px;
`;

const ContentWrapper = styled.div`
  width: 75%;
  margin: 0 auto;
  max-width: 100%;
  box-sizing: border-box;
  border-radius: 12px;
  overflow: hidden;
  padding: 40px 20px 20px;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    width: 100%;
    padding: 20px 12px;
  }
`;

const MainContentLayout = styled.div`
  display: flex;
  gap: 40px;
  width: 100%;
  margin-top: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
    display: grid;
    grid-template-areas: 
      "title"
      "tags"
      "art"
      "metadata"
      "download";
  }
`;

const LeftColumn = styled.div`
  flex-shrink: 0;
  width: 400px;
  display: flex;
  flex-direction: column;
  gap: -20px;

  @media (max-width: 768px) {
    width: 100%;
    grid-area: art;
  }
`;

const RightColumn = styled.div`
  flex-grow: 1;
  padding-top: 30px;

  @media (max-width: 768px) {
    padding-top: 0;
    display: contents;
  }
`;

const MetadataSection = styled.div`
  margin-top: 30px;

  @media (max-width: 768px) {
    grid-area: metadata;
    margin-top: 20px;
  }
`;

const MetadataHeader = styled.h3`
  font-family: 'Rubik', sans-serif;
  color: #2e2e2e;
  font-size: 1.1em;
  margin-bottom: 15px;
  font-weight: 500;

  @media (max-width: 768px) {
    text-align: center;
    font-size: 0.9em;
    margin-bottom: 10px;
  }
`;

const SongTitle = styled.h1`
  font-family: 'DM Serif Text', serif;
  color: #2e2e2e;
  text-align: left;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 20px;
  margin-top: ${props => props.$hasPills ? '20px' : '30px'};
  font-size: 2em;

  @media (max-width: 768px) {
    text-align: center;
    justify-content: center;
    grid-area: title;
    margin-bottom: 10px;
    margin-top: 10px;
    font-size: 1.5em;
  }
`;

const PillContainer = styled.div`
  position: absolute;
  top: 15px;
  left: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 2;
  flex-wrap: wrap;
  max-width: calc(100% - 30px);
`;

const FeaturedPill = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 8px;
  color: white;
  font-family: 'Rubik', sans-serif;
  font-size: 14px;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(4px);

  svg {
    margin-right: 5px;
    color: white;
  }

  @media (max-width: 480px) {
    padding: 2px 8px;
    font-size: 11px;
    border-radius: 6px;

    svg {
      font-size: 10px;
      margin-right: 3px;
    }
  }
`;

const TrendingPill = styled(FeaturedPill)`
  color: white;
  
  svg {
    margin-right: 3px;
    color: white;
  }
`;

const FireIconGroup = styled.span`
  display: inline-flex;
  align-items: center;
  margin-right: 5px;
`;

const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 0;
  margin-bottom: 10px;

  @media (max-width: 480px) {
    justify-content: center;
    & > a:nth-child(n+4) {
      display: none;
    }
  }
`;

const TagPill = styled.a`
  && { /* Increased specificity using double ampersand */
    display: flex;
    align-items: center;
    padding: 5px 10px;
    border: 1px solid ${(props) => props.color || '#000'};
    border-radius: 16px;
    color: ${(props) => props.color || '#000'};
    font-family: 'Rubik', sans-serif;
    font-size: 0.9em;
    font-weight: 400;
    text-decoration: none;
    transition: all 0.2s;

    &:hover {
      background-color: ${(props) => props.color || '#000'};
      color: #fff;
    }

    svg {
      margin-right: 5px;
    }

    @media (max-width: 480px) {
      padding: 2px 8px;
      font-size: 0.7em;
      border-radius: 10px;

      svg {
        display: none;
      }
    }
  }
`;

const BpmKeyContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    justify-content: center;
    gap: 6px;
    margin-bottom: 1.5rem;
  }
`;

const BpmKeyPill = styled.div`
  display: flex;
  align-items: center;
  padding: 5px 10px;
  border: 1px solid #696969;
  border-radius: 0px;
  color: #696969;
  font-family: 'Rubik', sans-serif;
  font-size: 0.9em;

  @media (max-width: 768px) {
    padding: 4px 8px;
    font-size: 0.8em;
  }
`;

const SongTags = styled.div`
  margin: 0 0 20px 0;
  text-align: center;

  @media (max-width: 768px) {
    grid-area: tags;
    margin: 0 0 10px 0;
  }
`;

const AlbumArtContainer = styled.div`
  width: 400px;
  height: 400px;
  margin: 20px auto;
  position: relative;
  border-radius: 15px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  background: #2E2E2E;

  /* Responsive adjustments */
  @media (max-width: 1024px) {
    width: 300px;
    height: 300px;
  }

  @media (max-width: 768px) {
    width: 250px;
    height: 250px;
    margin: 10px auto;
  }

  @media (max-width: 480px) {
    width: 90vw;
    height: 90vw;
  }
`;

const BlurImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: ${props => props.$blurDataUrl ? `url(${props.$blurDataUrl})` : 'none'};
  background-size: cover;
  background-position: center;
  filter: blur(10px);
  transform: scale(1.1);
  transition: opacity 0.3s ease-out;
  opacity: ${props => props.$isLoading ? 1 : 0};
`;

const MainAlbumArt = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: ${props => props.$image ? `url(${props.$image})` : 'none'};
  background-size: cover;
  background-position: center;
  opacity: ${props => props.$isLoaded ? 1 : 0};
  transition: opacity 0.3s ease-in-out;
`;

const PlayerContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 15px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.4) 60%, transparent 100%);
  backdrop-filter: blur(10px);
  border-bottom-left-radius: 15px;
  border-bottom-right-radius: 15px;

  @media (max-width: 480px) {
    padding: 15px;
    gap: 10px;
  }
`;

const PlayButton = styled.button`
  background: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #2e2e2e;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

  &:hover {
    transform: scale(1.1);
  }

  svg {
    font-size: 14px;
  }

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
  }

  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
    font-size: 12px;
  }
`;

const ProgressBar = styled.div`
  flex: 1;
  height: 60px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  transform: translateZ(0);
  will-change: transform;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  perspective: 1000;
  -webkit-perspective: 1000;

  @media (max-width: 480px) {
    height: 40px;
    border-radius: 8px;
  }
`;

const ProgressTracker = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  transform: translateZ(0);
  will-change: transform;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  perspective: 1000;
  -webkit-perspective: 1000;
`;

const ProgressFill = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: ${props => props.$isDragging ? props.$dragProgress : props.$progress}%;
  background: rgba(255, 255, 255, 0.2);
  transition: ${props => props.$isDragging ? 'none' : 'all 0.5s cubic-bezier(0.4, 0.0, 0.2, 1)'};
  transform-origin: left center;
  will-change: width, transform;
  transform: translateZ(0);
  backface-visibility: hidden;
  perspective: 1000;
  -webkit-backface-visibility: hidden;
  -webkit-perspective: 1000;
  -webkit-transform: translateZ(0);
`;

const WaveformBars = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 10px;
`;

const WaveBar = styled.div`
  width: 2px;
  height: ${props => props.$height * 1.5 || 20}%;
  background: ${props => props.$active ? 
    'linear-gradient(to bottom, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 1) 100%)' : 
    'linear-gradient(to bottom, rgba(255, 255, 255, 0.7) 0%, rgba(255, 255, 255, 0.3) 100%)'};
  border-radius: 1px;
  transition: 
    height 1.2s cubic-bezier(0.34, 1.56, 0.64, 1),
    transform 1.2s cubic-bezier(0.34, 1.56, 0.64, 1);
  transform-origin: center bottom;
  animation: ${props => props.$isReal ? 'none' : 'pulse 3s infinite ease-in-out'};
  transition-delay: ${props => props.$index * 25}ms;
  will-change: transform, height;

  &:hover {
    transform: scaleY(1.1);
  }

  @media (max-width: 480px) {
    width: 1.5px;
    height: ${props => props.$height * 0.6 || 10}%;
  }

  @keyframes pulse {
    0%, 100% {
      transform: scaleY(1);
    }
    50% {
      transform: scaleY(1.03);
    }
  }
`;

// DownloadButton with dynamic styles based on disabled state
const DownloadButton = styled.button`
  margin-top: 20px;
  padding: 15px 30px;
  font-size: 16px;
  font-family: 'Rubik', sans-serif;
  text-decoration: none;
  color: ${(props) => (props.disabled ? '#a0a0a0' : '#fff')};
  background-color: ${(props) => (props.disabled ? '#cccccc' : '#2e2e2e')};
  border: none;
  border-radius: 8px;
  display: inline-block;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: background-color 0.3s, color 0.3s;
  width: 100%;
  margin-left: auto;
  margin-right: auto;

  &:hover {
    background-color: ${(props) => (props.disabled ? '#cccccc' : '#444')};
  }
`;

// Updated DownloadDisclaimer message
const DownloadDisclaimer = styled.div`
  margin-top: 10px;
  text-align: center;
  max-width: 250px;
  margin-left: auto;
  margin-right: auto;

  p {
    font-family: 'Rubik', sans-serif;
    color: #a0a0a0;
    font-size: 12px;
    margin: 5px 0;
    white-space: pre-line;
  }

  .downloads-remaining {
    margin: 15px 0 4px;
    display: block;
  }

  .upgrade-text {
    display: block;
    margin-bottom: 15px;
  }

  a {
    color: #2e2e2e;
    text-decoration: underline;
    &:hover {
      color: #444;
    }
  }
`;

const DownloadStatusBar = styled.div`
  margin-top: 10px;
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

const NotchContainer = styled.div`
  width: 100%;
  height: 4px;
  background: transparent;
  display: flex;
  gap: 4px;
`;

const getNotchColor = (isActive, remainingDownloads) => {
  if (!isActive) return 'rgba(46, 46, 46, 0.15)';
  if (remainingDownloads <= 2) return 'rgba(220, 38, 38, 0.8)'; // Red
  if (remainingDownloads <= 5) return 'rgba(202, 138, 4, 0.85)'; // Darker yellow
  return '#2e2e2e'; // Default black
};

const getNotchOverlayColor = (isActive, remainingDownloads) => {
  if (!isActive) return 'transparent';
  if (remainingDownloads <= 2) return 'rgba(220, 38, 38, 0.1)';
  if (remainingDownloads <= 5) return 'rgba(202, 138, 4, 0.1)';
  return 'rgba(46, 46, 46, 0.1)';
};

const Notch = styled.div`
  flex: ${props => props.$isFree ? '1' : '1'};
  height: 100%;
  background: ${props => getNotchColor(props.$active, props.$remainingDownloads)};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;

  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => getNotchOverlayColor(props.$active, props.$remainingDownloads)};
    transform: scaleX(${props => props.$active ? '1' : '0'});
    transform-origin: left;
    transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  }
`;

const DownloadText = styled.div`
  font-family: 'Rubik', sans-serif;
  color: ${props => {
    if (props.$remainingDownloads <= 2) return 'rgba(220, 38, 38, 0.8)';
    if (props.$remainingDownloads <= 5) return 'rgba(202, 138, 4, 0.85)';
    return '#2e2e2e';
  }};
  text-align: center;
  font-size: 12px;
  margin: 5px 0;
  opacity: 0.8;
`;

const PremierNotchContainer = styled(NotchContainer)`
  background: linear-gradient(90deg, rgba(59, 130, 246, 0.1), rgba(59, 130, 246, 0.05));
`;

const PremierNotch = styled.div`
  flex: 1;
  height: 100%;
  background: linear-gradient(90deg, #3B82F6, #60A5FA);
  border-radius: 2px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 200%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );
    animation: ${shimmer} 2s infinite;
  }
`;

const PremierDownloadText = styled(DownloadText)`
  color: #3B82F6;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-weight: 500;

  svg {
    font-size: 14px;
  }
`;

const DownloadSection = styled.div`
  width: 400px;
  margin: 0 auto;

  @media (max-width: 768px) {
    width: 100%;
    grid-area: download;
  }
`;

const SongPage = () => {
  const { songId } = useParams();
  const { user, getDownloadStatus } = useContext(AuthContext);
  const { 
    currentSongId,
    isPlaying: contextIsPlaying,
    currentProgress,
    loadAudioBuffer,
    playSong,
    pausePlayback,
    seekTo,
    songsLoaded
  } = useSongContext();
  const [song, setSong] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [albumArtSrc, setAlbumArtSrc] = useState('');
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState('');
  const [metaImageUrl, setMetaImageUrl] = useState('');
  const [imageLoaded, setImageLoaded] = useState(false);
  const [blurDataUrl, setBlurDataUrl] = useState('');
  const [trendingRank, setTrendingRank] = useState(0);
  const [downloadStatus, setDownloadStatus] = useState({
    canDownload: false,
    remainingDownloads: 0,
    weeklyLimit: 0,
    message: '',
    unlimited: false
  });
  const [waveformData, setWaveformData] = useState(null);
  const [isWaveformQueued, setIsWaveformQueued] = useState(false);
  const progressRef = useRef(null);
  const isTransitioning = useRef(false);
  const [playStartTime, setPlayStartTime] = useState(null);
  const hasTrackedRef = useRef(false);
  const [duration, setDuration] = useState(0);

  // Function to get canonical URL without www
  const getCanonicalUrl = () => {
    const url = new URL(window.location.href);
    // Remove www if present
    url.hostname = url.hostname.replace(/^www\./, '');
    return url.toString();
  };

  // Generate a tiny blur placeholder
  const generateBlurPlaceholder = (width = 20) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = width;
    canvas.height = width; // Square image

    ctx.fillStyle = '#2E2E2E';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    return canvas.toDataURL('image/jpeg', 0.1);
  };

  // Add trackPlay function wrapped in useCallback
  const trackPlay = useCallback(async (songId, playDuration) => {
    console.log('Tracking play:', { songId, playDuration });
    
    try {
      const token = localStorage.getItem('token');
      const url = `${process.env.REACT_APP_API_BASE_URL}/songs/${songId}/play`;

      const headers = {
        'Content-Type': 'application/json'
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ playDuration })
      });

      if (!response.ok) {
        console.error('Play tracking failed:', {
          status: response.status,
          statusText: response.statusText
        });
        return;
      }

      await response.json();
      console.log('Play tracked successfully for song:', songId);
    } catch (error) {
      console.error('Error tracking play:', {
        error: error.message,
        songId,
        playDuration
      });
    }
  }, []);

  // Add formatTime function
  const formatTime = (seconds) => {
    if (isNaN(seconds) || seconds === Infinity) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60).toString().padStart(2, '0');
    return `${mins}m${secs}s`;
  };

  useEffect(() => {
    const fetchSong = async () => {
      try {
        const songData = await getSongById(songId);
        setSong(songData);
        console.log('Fetched song:', songData);

        // Get trending rank
        try {
          const trendingResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/songs/trending`);
          if (trendingResponse.ok) {
            const trendingSongs = await trendingResponse.json();
            console.log('Trending songs:', trendingSongs);
            console.log('Current song ID:', songId);
            
            // Ensure both IDs are strings for comparison
            const rank = trendingSongs.findIndex(s => String(s.song_id) === String(songId)) + 1;
            console.log('Calculated rank:', rank);
            setTrendingRank(rank > 0 ? rank : 0);
          } else {
            console.error('Failed to fetch trending songs:', await trendingResponse.text());
            setTrendingRank(0);
          }
        } catch (trendingError) {
          console.error('Error fetching trending data:', trendingError);
          setTrendingRank(0);
        }

        // Fetch signed URL for audio file
        if (songData.song_file_wm) {
          const audioPath = songData.song_file_wm.startsWith('/') 
            ? songData.song_file_wm.slice(1) 
            : songData.song_file_wm;
          
          const signedAudioUrl = await getSignedUrl(audioPath);
          if (signedAudioUrl) {
            setAudioUrl(signedAudioUrl);
          }
        }

        // Handle album art loading
        if (songData.song_art) {
          // Set blur placeholder immediately
          setBlurDataUrl(generateBlurPlaceholder());
          
          const artPath = songData.song_art.startsWith('/') 
            ? songData.song_art.slice(1) 
            : songData.song_art;
          
          // Get two signed URLs - one for display and one for meta tags
          const displaySignedArtUrl = await getSignedUrl(artPath); // Default 5 min expiration for display
          const metaSignedArtUrl = await getSignedUrl(artPath, 3600); // 1 hour expiration for meta tags
          
          if (displaySignedArtUrl) {
            // Preload the image for display
            const img = new Image();
            img.onload = () => {
              setAlbumArtSrc(displaySignedArtUrl);
              setImageLoaded(true);
            };
            img.src = displaySignedArtUrl;
            
            // Set meta image URL separately with longer expiration
            setMetaImageUrl(metaSignedArtUrl);
          } else {
            setAlbumArtSrc('/images/categories/ambient.jpg');
            setMetaImageUrl('https://tempostock.com/images/social.png');
          }
        } else {
          setAlbumArtSrc('/images/categories/ambient.jpg');
          setMetaImageUrl('https://tempostock.com/images/social.png');
        }
      } catch (error) {
        console.error('Error fetching song:', error);
        setAlbumArtSrc('/images/categories/ambient.jpg');
        setMetaImageUrl('https://tempostock.com/images/social.png');
      }
    };

    fetchSong();
  }, [songId]);

  useEffect(() => {
    const fetchDownloadStatus = async () => {
      if (!user) return;

      const status = await getDownloadStatus();
      console.log('Download status:', status);
      console.log('Is Premier user?', user.account_type === 'Premier');
      console.log('Is unlimited?', status.unlimited);
      console.log('User account type:', user.account_type);
      console.log('Worker version:', status.workerVersion || 'not specified');
      setDownloadStatus(status);
    };

    fetchDownloadStatus();
  }, [user, songId, getDownloadStatus]);

  // Load waveform data when audio URL is available
  useEffect(() => {
    const loadWaveform = async () => {
      if (!song?.song_file_wm || isWaveformQueued || waveformData) return;

      try {
        setIsWaveformQueued(true);

        // Get signed URL for the audio file
        const signedUrl = await getSignedUrl(song.song_file_wm);
        if (!signedUrl) {
          throw new Error('Failed to get signed URL');
        }

        // Add to waveform generation queue
        waveformQueue.push({
          signedUrl,
          setWaveformData,
          setIsLoading: () => {}, // No-op since we don't use isLoading
          markComplete: () => setIsWaveformQueued(false)
        });

        // Process queue in background
        requestIdle(() => processWaveformQueue());

      } catch (error) {
        console.error('Error queueing waveform generation:', error);
        setIsWaveformQueued(false);
      }
    };

    loadWaveform();
  }, [song?.song_file_wm, isWaveformQueued, waveformData]);

  // Add effect to monitor trending rank changes
  useEffect(() => {
    console.log('Trending rank updated:', trendingRank);
  }, [trendingRank]);

  // Use real waveform data if available, otherwise generate placeholder
  const displayWaveform = useMemo(() => {
    if (waveformData) {
      // Map the waveform data to our desired display range (2-65)
      const minDisplayHeight = 2;
      const maxDisplayHeight = 65;
      const displayRange = maxDisplayHeight - minDisplayHeight;

      const originalMin = 20; // Expected min from generateWaveformData's normalization
      const originalMax = 90; // Expected max from generateWaveformData's normalization
      const originalRange = originalMax - originalMin;

      return {
        data: waveformData.map(value => {
          // Clamp value to the expected original range first
          const clampedValue = Math.max(originalMin, Math.min(originalMax, value));
          // Normalize from original range (0-1)
          let normalizedOriginal = 0;
          if (originalRange > 0) {
            normalizedOriginal = (clampedValue - originalMin) / originalRange;
          }
          // Scale to new display range
          return Math.round(minDisplayHeight + normalizedOriginal * displayRange);
        }),
        isReal: true
      };
    }
    
    // Fallback to placeholder waveform if real data isn't loaded yet
    const barsCount = 48;
    const bars = new Array(barsCount);
    for (let i = 0; i < barsCount; i++) {
      const baseHeight = Math.sin((i / barsCount) * Math.PI * 4) * 25 + 30;
      const randomVariation = Math.random() * 8 - 4;
      const height = Math.max(2, Math.min(65, baseHeight + randomVariation));
      bars[i] = Math.round(height);
    }
    
    // Smooth placeholder data
    for (let i = 1; i < barsCount - 1; i++) {
      bars[i] = Math.round((bars[i - 1] + bars[i] * 2 + bars[i + 1]) / 4);
    }
    
    return {
      data: bars,
      isReal: false
    };
  }, [waveformData]);

  // Calculate active bar index based on progress
  const activeBarIndex = useMemo(() => {
    if (!isPlaying) return -1;
    return Math.floor((displayWaveform.data.length * (currentProgress || 0)) / 100);
  }, [isPlaying, currentProgress, displayWaveform.data.length]);

  // Handle play/pause toggle
  const handlePlayToggle = async () => {
    if (!song?.song_id || !audioUrl || isTransitioning.current) return;
    
    try {
      isTransitioning.current = true;
      
      if (isPlaying) {
        // This song is currently playing - pause it
        pausePlayback();
        setIsPlaying(false);
        
        // Track play duration when pausing
        if (playStartTime && !hasTrackedRef.current) {
          const playDuration = Math.floor((Date.now() - playStartTime) / 1000);
          if (playDuration >= 5) {
            hasTrackedRef.current = true;
            trackPlay(song.song_id, playDuration);
          }
          setPlayStartTime(null);
        }
      } else {
        // Start playing this song
        if (!songsLoaded[song.song_id]) {
          await loadAudioBuffer(song.song_id, song.song_file_wm);
        }
        
        const success = await playSong(song.song_id, song.song_file_wm, 0);
        
        if (success) {
          setIsPlaying(true);
          setPlayStartTime(Date.now());
          hasTrackedRef.current = false;
        } else {
          console.error('Failed to play song');
        }
      }
    } catch (error) {
      console.error('Error toggling playback:', error);
    } finally {
      isTransitioning.current = false;
    }
  };

  // Handle progress bar click
  const handleProgressClick = async (e) => {
    if (!progressRef.current || !song?.song_id || isTransitioning.current) return;
    
    const rect = progressRef.current.getBoundingClientRect();
    const position = ((e.clientX - rect.left) / rect.width) * 100;
    
    try {
      isTransitioning.current = true;
      
      if (!isPlaying) {
        // If not playing, start playing from clicked position
        if (!songsLoaded[song.song_id]) {
          await loadAudioBuffer(song.song_id, song.song_file_wm);
        }
        const success = await playSong(song.song_id, song.song_file_wm, position);
        if (success) {
          setIsPlaying(true);
        }
      } else {
        // If already playing, just seek
        seekTo(position);
      }
    } catch (error) {
      console.error('Error handling progress click:', error);
    } finally {
      isTransitioning.current = false;
    }
  };

  // Sync playing state with context
  useEffect(() => {
    if (song?.song_id === currentSongId) {
      setIsPlaying(contextIsPlaying);
    } else if (isPlaying) {
      setIsPlaying(false);
    }
  }, [currentSongId, contextIsPlaying, song?.song_id, isPlaying]);

  // Add effect to track plays when component unmounts or song changes
  useEffect(() => {
    if (!isPlaying && playStartTime && !hasTrackedRef.current && song?.song_id) {
      const playDuration = Math.floor((Date.now() - playStartTime) / 1000);
      if (playDuration >= 5) {
        hasTrackedRef.current = true;
        trackPlay(song.song_id, playDuration);
      }
    }
    
    return () => {
      if (isPlaying && playStartTime && !hasTrackedRef.current && song?.song_id) {
        const playDuration = Math.floor((Date.now() - playStartTime) / 1000);
        if (playDuration >= 5) {
          hasTrackedRef.current = true;
          trackPlay(song.song_id, playDuration);
        }
      }
    };
  }, [isPlaying, playStartTime, song?.song_id, trackPlay]);

  // Add effect to get duration when song is loaded
  useEffect(() => {
    const loadDuration = async () => {
      if (song?.song_id && !duration) {
        if (!songsLoaded[song.song_id]) {
          await loadAudioBuffer(song.song_id, song.song_file_wm);
        }
        const sound = audioService.sounds.get(song.song_id)?.sound;
        if (sound) {
          setDuration(sound.duration());
        }
      }
    };
    loadDuration();
  }, [song?.song_id, song?.song_file_wm, songsLoaded, duration, loadAudioBuffer]);

  const handleDownload = async () => {
    if (!user) {
      alert('Please log in to download tracks.');
      return;
    }

    setDownloadLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/auth/download/${song.song_id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const downloadUrl = data.downloadUrl;

        const res = await fetch(downloadUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (res.ok) {
          const blob = await res.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;

          const contentDisposition = res.headers.get('Content-Disposition');
          let filename = `${song.song_title}.mp3`;

          if (contentDisposition && contentDisposition.includes('filename=')) {
            filename = contentDisposition.split('filename=')[1].replace(/"/g, '');
          }

          link.download = filename;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);

          // Refresh download status after successful download
          const newStatus = await getDownloadStatus();
          setDownloadStatus(newStatus);
        } else {
          const errorData = await res.json();
          console.error('Download failed:', errorData.error);
          alert(`Download failed: ${errorData.error}`);
        }
      } else if (response.status === 403) {
        const data = await response.json();
        alert(data.error || 'Download limit reached.');
        const newStatus = await getDownloadStatus();
        setDownloadStatus(newStatus);
      } else {
        const errorData = await response.json();
        console.error('Download failed:', errorData.error);
        alert(`Download failed: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Error during download:', error);
      alert('An unexpected error occurred during the download.');
    } finally {
      setDownloadLoading(false);
    }
  };

  // Function to get trending text
  const getTrendingText = () => {
    console.log('Getting trending text for rank:', trendingRank);
    if (trendingRank === 1) {
      return (
        <>
          <FireIconGroup>
            <FaFire />
          </FireIconGroup>
          Trending #1
        </>
      );
    }
    if (trendingRank > 1 && trendingRank <= 10) {
      return (
        <>
          <FireIconGroup>
            <FaFire />
          </FireIconGroup>
          Top 10 Trending
        </>
      );
    }
    if (trendingRank > 10 && trendingRank <= 50) {
      return (
        <>
          <FireIconGroup>
            <FaFire />
          </FireIconGroup>
          Top 50 Trending
        </>
      );
    }
    return '';
  };

  if (!song) return <p>Loading...</p>;

  const canonicalUrl = getCanonicalUrl();

  return (
    <PageContainer>
      <Helmet>
        <title>{song.song_title} | Tempo Stock</title>
        <meta name="description" content={`Listen to ${song.song_title} by ${song.song_artist} on Tempo Stock - Royalty-free music for your projects.`} />
        
        {/* Open Graph Facebook */}
        <meta property="og:type" content="music.song" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={`${song.song_title} | Tempo Stock`} />
        <meta property="og:description" content={`Listen to ${song.song_title} by ${song.song_artist} on Tempo Stock - Royalty-free music for your projects.`} />
        <meta property="og:image" content={metaImageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={canonicalUrl} />
        <meta name="twitter:title" content={`${song.song_title} | Tempo Stock`} />
        <meta name="twitter:description" content={`Listen to ${song.song_title} by ${song.song_artist} on Tempo Stock - Royalty-free music for your projects.`} />
        <meta name="twitter:image" content={metaImageUrl} />
        
        {/* Additional Meta Tags */}
        <meta property="og:site_name" content="Tempo Stock" />
        <meta property="og:audio" content={song.song_file_wm} />
        <meta property="og:audio:type" content="audio/mpeg" />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>
      <ContentWrapper>
        <MainContentLayout>
          <LeftColumn>
            {albumArtSrc && (
              <AlbumArtContainer>
                {(song?.song_featured || (trendingRank > 0 && trendingRank <= 50)) && (
                  <PillContainer>
                    {song?.song_featured && (
                      <FeaturedPill>
                        <FaStar />
                        Featured
                      </FeaturedPill>
                    )}
                    {trendingRank > 0 && trendingRank <= 50 && (
                      <TrendingPill>
                        {getTrendingText()}
                      </TrendingPill>
                    )}
                  </PillContainer>
                )}
                <BlurImage $blurDataUrl={blurDataUrl} $isLoading={!imageLoaded} />
                <MainAlbumArt $image={albumArtSrc} $isLoaded={imageLoaded} />
                {song && (
                  <PlayerContainer>
                    <PlayButton onClick={handlePlayToggle}>
                      {isPlaying ? <FaPause /> : <FaPlay />}
                    </PlayButton>
                    <ProgressBar 
                      ref={progressRef}
                      onClick={handleProgressClick}
                    >
                      <ProgressTracker>
                        <ProgressFill $progress={currentProgress || 0} />
                      </ProgressTracker>
                      <WaveformBars>
                        {displayWaveform.data.map((height, index) => (
                          <WaveBar 
                            key={index}
                            $height={height}
                            $index={index}
                            $isReal={displayWaveform.isReal}
                            $active={isPlaying && index <= activeBarIndex}
                          />
                        ))}
                      </WaveformBars>
                    </ProgressBar>
                  </PlayerContainer>
                )}
              </AlbumArtContainer>
            )}

            <DownloadSection>
              {user ? (
                <>
                  <DownloadButton
                    onClick={handleDownload}
                    disabled={!downloadStatus.canDownload || downloadLoading}
                  >
                    {downloadLoading ? 'Downloading...' : 'Download Track'}
                  </DownloadButton>

                  {downloadStatus.unlimited ? (
                    <DownloadStatusBar>
                      <PremierNotchContainer>
                        <PremierNotch />
                      </PremierNotchContainer>
                      <PremierDownloadText>
                        <FaCrown />
                        Unlimited downloads
                      </PremierDownloadText>
                    </DownloadStatusBar>
                  ) : (
                    <DownloadStatusBar>
                      <NotchContainer>
                        {user?.account_type === 'Free' ? (
                          <Notch 
                            $isFree={true} 
                            $active={downloadStatus.remainingDownloads > 0}
                            $remainingDownloads={downloadStatus.remainingDownloads}
                          />
                        ) : (
                          Array.from({ length: 10 }).map((_, index) => (
                            <Notch 
                              key={index}
                              $active={index < downloadStatus.remainingDownloads}
                              $remainingDownloads={downloadStatus.remainingDownloads}
                            />
                          ))
                        )}
                      </NotchContainer>
                      <DownloadText $remainingDownloads={downloadStatus.remainingDownloads}>
                        {downloadStatus.remainingDownloads} Download{downloadStatus.remainingDownloads !== 1 ? 's' : ''} Remaining
                      </DownloadText>
                    </DownloadStatusBar>
                  )}

                  <DownloadDisclaimer>
                    <p>
                      {!downloadStatus.unlimited && !downloadStatus.canDownload && (
                        <span className="upgrade-text">
                          {user?.account_type === 'Free' ? (
                            <a href="/pricing">Upgrade to Pro for more downloads.</a>
                          ) : user?.account_type === 'Pro' && (
                            <a href="/pricing">Upgrade to Premier for unlimited downloads.</a>
                          )}
                        </span>
                      )}
                      By downloading you agree to our TOS.
                      {user?.account_type === 'Free' && "\nFree users must credit Tempo Stock with track usage."}
                    </p>
                  </DownloadDisclaimer>
                </>
              ) : (
                <>
                  <DownloadButton disabled>
                    Download Track
                  </DownloadButton>
                  <DownloadDisclaimer>
                    <p>
                      Please <a href="/login">log in</a> to download tracks.
                    </p>
                  </DownloadDisclaimer>
                </>
              )}
            </DownloadSection>
          </LeftColumn>

          <RightColumn>
            <SongTitle>
              {song?.song_title}
            </SongTitle>
            
            <SongTags>
              <TagContainer>
                {song.song_tags.map((tag) => {
                  const IconComponent = tagIcons[tag] || tagIcons.Piano;
                  return (
                    <TagPill
                      key={tag}
                      color={tagColors[tag] || '#000'}
                      href={`/${tag.toLowerCase()}`}
                    >
                      <IconComponent />
                      {tag}
                    </TagPill>
                  );
                })}
              </TagContainer>
            </SongTags>

            <MetadataSection>
              <MetadataHeader>Metadata</MetadataHeader>
              <BpmKeyContainer>
                <BpmKeyPill>{formatTime(duration)}</BpmKeyPill>
                <BpmKeyPill>{song.song_bpm}</BpmKeyPill>
                <BpmKeyPill>{song.song_key}</BpmKeyPill>
              </BpmKeyContainer>
            </MetadataSection>
          </RightColumn>
        </MainContentLayout>
        <SimilarStockSection currentSong={song} />
      </ContentWrapper>
    </PageContainer>
  );
};

export default SongPage;